import React, { useEffect, useMemo, useState } from 'react';
import { Pagination, Stack, Typography } from '@mui/material';

// ---------------------------------------------------------------------------
// Shared search + pagination for every token listing page.
//
// The listing pages used to search only the rows already on screen: paged
// endpoints (Hybrid, Hedge, FxPairs, Stocks) loaded 10 rows at a time and
// filtered just those, and several pages (XMG, Wraps, DCN, PDCN, Leverage,
// Nexus, Delisted) wired the search box to nothing at all. Each page now
// loads its whole list and hands it to this hook, which searches all of it
// and pages the result, so search always covers every token.
//
// Matching: case-insensitive; every word typed must appear in one of the
// token's fields (symbol, identity symbol, name, issuer, ...). Results are
// ranked exact symbol > symbol prefix > name prefix > anywhere, so typing
// "btc" puts hBTC/BTC at the top instead of wherever they happen to sit.
// ---------------------------------------------------------------------------

const DEFAULT_FIELDS = [
  'identitySymbol',
  'symbol',
  'token_symbol',
  'name',
  'token_name',
  'tokenName',
  'token_issuer',
  'issuer',
  'reference_symbol',
  'base_symbol'
];

const norm = (v) =>
  String(v ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[‐-―]/g, '-')
    .trim();

const symbolsOf = (row) =>
  [row.identitySymbol, row.symbol, row.token_symbol].filter(Boolean).map(norm);
const namesOf = (row) =>
  [row.name, row.token_name, row.tokenName].filter(Boolean).map(norm);

function score(row, query, terms, fields) {
  const hay = fields.map((f) => norm(row[f])).filter(Boolean);
  if (!terms.every((t) => hay.some((h) => h.includes(t)))) return -1;
  const syms = symbolsOf(row);
  const names = namesOf(row);
  if (syms.some((s) => s === query)) return 100;
  // "btc" should find hBTC / xBTC too, not just BTC*: strip a 1-letter prefix.
  if (syms.some((s) => s.startsWith(query) || s.slice(1) === query)) return 80;
  if (syms.some((s) => s.slice(1).startsWith(query))) return 70;
  if (names.some((n) => n.startsWith(query))) return 60;
  if (syms.some((s) => s.includes(query))) return 40;
  if (names.some((n) => n.split(/\s+/).some((w) => w.startsWith(terms[0]))))
    return 30;
  return 10;
}

export function filterTokens(rows, search, fields = DEFAULT_FIELDS) {
  const list = Array.isArray(rows) ? rows : [];
  const query = norm(search);
  if (!query) return list;
  const terms = query.split(/\s+/).filter(Boolean);
  return list
    .map((row, i) => ({ row, i, s: score(row, query, terms, fields) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => b.s - a.s || a.i - b.i) // stable within a score
    .map((x) => x.row);
}

/**
 * @param rows      the FULL token list for the page
 * @param search    raw text from the search box
 * @param options   { perPage = 25, fields }
 */
export function useTokenListing(rows, search, { perPage = 25, fields } = {}) {
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);

  // Debounce typing; any new search starts from page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search || '');
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(
    () => filterTokens(rows, debounced, fields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, debounced]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const current = Math.min(page, totalPages);

  // Don't strand the user on a page that no longer exists.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(
    () => filtered.slice((current - 1) * perPage, current * perPage),
    [filtered, current, perPage]
  );

  return {
    pageRows,
    filteredCount: filtered.length,
    totalCount: Array.isArray(rows) ? rows.length : 0,
    page: current,
    setPage,
    totalPages,
    perPage,
    query: debounced
  };
}

/** "Showing 1-25 of 143" + page buttons, styled to match the theme. */
export function ListingFooter({ listing, loaded = true, noun = 'tokens' }) {
  const { page, perPage, filteredCount, totalCount, totalPages, setPage, query } =
    listing;
  const start = filteredCount ? (page - 1) * perPage + 1 : 0;
  const end = Math.min(page * perPage, filteredCount);

  let text;
  if (!loaded) text = `Loading ${noun}…`;
  else if (!filteredCount)
    text = query ? `No ${noun} match "${query}"` : `No ${noun} found`;
  else
    text =
      `Showing ${start}–${end} of ${filteredCount}` +
      (query && filteredCount !== totalCount
        ? ` (filtered from ${totalCount})`
        : '');

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mt: 2, mb: 2, px: 1 }}
    >
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
        {text}
      </Typography>
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => {
            setPage(value);
          }}
          shape="rounded"
          siblingCount={1}
          sx={{
            '& .MuiPaginationItem-root': { color: '#e5e7eb' },
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: '#34D399',
              color: '#000'
            },
            '& .MuiPaginationItem-root.Mui-selected:hover': {
              backgroundColor: '#10B981'
            }
          }}
        />
      )}
    </Stack>
  );
}

export default useTokenListing;
